import type { PartnerAppointmentType, PartnerAvailableSlot, PartnerLanding } from "@cabocheck/shared";
import Loader from "@cabocheck/shared/src/components/core/Loader";
import { Calendar, CircleCheck, Clock, Mail, Package2, Syringe, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { formatDate, fromUnix } from "~/utils/date";
import { AppointmentType } from "./AppointmentType";
import { CalendarLayout } from "./CalendarLayout";
import { CalendarView } from "./CalendarView";
import { PatientDetails, type TPatientSchema } from "./PatientDetails";
import { PublicRegistration } from "./PublicRegistration";
import invariant from "invariant";
import { format } from "date-fns";
import type { PartnerAppointmentTypeListResponse } from "~/routes/api.v1.partner.appointment_types";
import type {
  CreatePartnerAppointmentInput,
  CreatePartnerAppointmentResponse,
} from "~/routes/api.v1.partner.appointments";

export function PublicCalendar({
  partnerLanding,
  appointmentType,
  medicalTestProfileGuid,
  initialAppointmentTypes,
  mode = "booking",
}: Props) {
  const [type, setType] = useState<PartnerAppointmentType | null>(null);
  const [date, setDate] = useState<PartnerAvailableSlot | null>(null);
  const [patientDetails, setPatientDetails] = useState<TPatientSchema | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const title = mode === "registration" ? t("Anmeldung ohne Termin") : t("Terminfindung");
  const microSite = partnerLanding;
  const [appointmentTypes, setAppointmentTypes] = useState<PartnerAppointmentType[]>(initialAppointmentTypes ?? []);

  useEffect(() => {
    if (!microSite || initialAppointmentTypes) {
      return;
    }

    const fetchAppointmentTypes = async () => {
      const response = await fetch(
        `/api/v1/partner/appointment_types?partner_guid=${encodeURIComponent(microSite.partner_guid)}`,
      );
      if (!response.ok) {
        throw new Error(`Failed to load appointment types: ${response.status}`);
      }
      const data = (await response.json()) as PartnerAppointmentTypeListResponse;
      setAppointmentTypes(data?.partner_appointment_types ?? []);
    };

    fetchAppointmentTypes();
  }, [initialAppointmentTypes, microSite]);

  const createAppointmentMutation = useMutation<CreatePartnerAppointmentResponse, Error, CreatePartnerAppointmentInput>(
    {
      mutationFn: async (payload) => {
        const res = await fetch("/api/v1/partner/appointments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          throw new Error(res.statusText || "unknown_error");
        }
        const json = await res.json();
        return json as CreatePartnerAppointmentResponse;
      },
    },
  );

  const handleSubmitBooking = async (data: TPatientSchema) => {
    if (!date?.start || !type?.partner_appointment_type_guid || !microSite?.partner_guid) {
      return;
    }

    setBookingError(null);

    const payload: CreatePartnerAppointmentInput = {
      partner_guid: microSite.partner_guid,
      start: typeof date.start === "string" ? Number(date.start) : date.start,
      partner_appointment_type_guid: type.partner_appointment_type_guid,
      client_first_name: data.firstName,
      client_last_name: data.lastName,
      client_email: data.email,
      client_telephone: data.phone,
      client_gender: data.gender || undefined,
      client_dob: data.dateOfBirth ? format(data.dateOfBirth, "yyyy-MM-dd") : undefined,
      medical_test_profile_guid: medicalTestProfileGuid || data.medicalTestProfileGuid || undefined,
      client_street: data.clientStreet || undefined,
      client_zip: data.clientZip || undefined,
      client_city: data.clientCity || undefined,
      client_insurance_type: data.clientInsuranceType || undefined,
      client_insurance_number: data.clientInsuranceNumber || undefined,
      client_insurance_company_guid: data.clientInsuranceCompanyGuid || undefined,
    };

    let appointment_response: CreatePartnerAppointmentResponse;
    try {
      appointment_response = await createAppointmentMutation.mutateAsync(payload);
    } catch (error) {
      const message = error instanceof Error ? error.message : "unknown_error";
      setBookingError(
        message === "unavailable_product_inventory"
          ? t("Für diesen Termin ist aktuell kein Bestand verfügbar. Bitte wählen Sie einen anderen Termin.")
          : t("Die Terminbuchung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut."),
      );
      return;
    }

    invariant(appointment_response, "Appointment response is required");
    const partner_appointment_guid = appointment_response.partner_appointment_guid;
    const attachment_upload_token = appointment_response.attachment_upload_token;
    const files = data.files;
    const partner_guid = microSite.partner_guid;
    const query_params = new URLSearchParams({
      client_bundle_id: "com.cabocheck.web",
      device_locale: i18n.language,
    });

    if (files && files.length > 0) {
      invariant(partner_appointment_guid, "Appointment GUID is required");
      invariant(attachment_upload_token, "Appointment upload token is required");
      try {
        for (const file of files) {
          const form_data = new FormData();
          form_data.append("attachment", file);
          form_data.append("content_type", file.type);

          query_params.set("partner_guid", partner_guid);
          const uploadResponse = await fetch(
            `/api/v1/partner/appointments/${partner_appointment_guid}/attachment_notes?${query_params}`,
            {
              method: "POST",
              headers: { "Appointment-Upload-Token": attachment_upload_token },
              body: form_data,
            },
          );
          const uploadResult = await uploadResponse.json();
          if (!uploadResponse.ok || !uploadResult.success) {
            throw new Error(uploadResult.error || "attachment_upload_failed");
          }
        }
      } catch {
        setBookingError(
          t(
            "Der Termin wurde erstellt, aber die Dokumente konnten nicht hochgeladen werden. Bitte versuchen Sie es erneut.",
          ),
        );
        return;
      }
    }

    setPatientDetails(data);
  };

  const infos = useMemo(() => {
    const vaccinationNames = type?.vaccinations
      ?.map((vaccination) => vaccination.vaccination_name || t("Impfung"))
      .join(", ");

    return [
      ...(type
        ? [
            { icon: <Clock />, label: `${type?.duration} min` },
            { icon: <Mail />, label: type.name },
            ...(vaccinationNames ? [{ icon: <Syringe />, label: vaccinationNames }] : []),
            ...(type.product_name ? [{ icon: <Package2 />, label: type.product_name }] : []),
          ]
        : []),
      ...(date
        ? [
            {
              icon: <Calendar />,
              label: `${formatDate(fromUnix(date.start), "daytime")} - ${formatDate(fromUnix(date.end), "time")}`,
            },
          ]
        : []),
      ...(patientDetails ? [{ icon: <User />, label: `${patientDetails.firstName} ${patientDetails.lastName}` }] : []),
    ];
  }, [type, date, patientDetails, t]);

  if (appointmentTypes.length === 0 && !appointmentType) {
    return null;
  }

  if (!microSite) {
    return (
      <CalendarLayout title={t("Terminfindung")} infos={infos}>
        <div className="flex h-full items-center justify-center">
          <Loader className="size-10" />
        </div>
      </CalendarLayout>
    );
  }

  if (!type) {
    const finalAppointmentTypes = appointmentType
      ? appointmentType.partner_appointment_type_guid
        ? [
            appointmentType,
            ...appointmentTypes.filter(
              (t) => t.partner_appointment_type_guid !== appointmentType.partner_appointment_type_guid,
            ),
          ]
        : [appointmentType, ...appointmentTypes]
      : appointmentTypes;

    return (
      <CalendarLayout
        title={title}
        logo={microSite.logo_url}
        infos={infos}
        partnerName={microSite.partner_name}
        description={
          mode === "registration"
            ? t("Wählen Sie die gewünschte Leistung für Ihre Anmeldung.")
            : t("publicCalendarTypeSelectDescription")
        }
      >
        <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-2 xl:p-8">
          {finalAppointmentTypes
            .filter((t) => mode !== "registration" || !t.system_managed_source)
            .map((t, ind) => (
              <AppointmentType
                onClick={() => setType(t as PartnerAppointmentType)}
                key={t.partner_appointment_type_guid || ind}
                description={t.description ?? ""}
                duration={mode === "registration" ? undefined : t.duration}
                title={t.name}
                registration={mode === "registration"}
                bookingMode={mode === "registration" ? "direct" : (t.booking_mode as "direct" | "request")}
                requestHintText={mode === "registration" ? undefined : t.request_hint_text}
              />
            ))}
        </div>
      </CalendarLayout>
    );
  }

  if (mode === "registration") {
    return <PublicRegistration partnerLanding={microSite} appointmentType={type} onBack={() => setType(null)} />;
  }

  if (!date) {
    return (
      <CalendarLayout
        title={t("Terminfindung")}
        logo={microSite.logo_url}
        infos={infos}
        partnerName={microSite.partner_name}
        onBack={() => setType(null)}
        description={t("publicCalendarDateSelectDescription")}
      >
        <CalendarView
          onSelect={(selectedDate) => {
            setBookingError(null);
            setDate(selectedDate);
          }}
          type={type}
          partnerGuid={microSite.partner_guid}
        />
      </CalendarLayout>
    );
  }

  if (!patientDetails) {
    // there is a very wierd variable finalAppointmentTypes which I don't understand at all
    // I will just get the type from the guid
    const appointment_type = appointmentTypes.find(
      (t) => t.partner_appointment_type_guid === type?.partner_appointment_type_guid,
    );

    return (
      <CalendarLayout
        title={t("Terminfindung")}
        logo={microSite.logo_url}
        infos={infos}
        partnerName={microSite.partner_name}
        onBack={() => setDate(null)}
        description={t("publicCalendarPatientDetailsDescription")}
      >
        <PatientDetails
          appointmentType={appointment_type ?? type}
          bookingError={bookingError}
          medicalTestProfileGuid={medicalTestProfileGuid}
          onSubmit={handleSubmitBooking}
          medicalTestProfiles={microSite.medical_test_profiles ?? []}
        />
      </CalendarLayout>
    );
  }

  const isRequestMode = type?.booking_mode === "request";

  return (
    <div className="flex max-w-[500px] items-center justify-center">
      <div className="flex flex-col items-center space-y-4 rounded-[32px] border bg-white p-8 shadow-lg">
        <img src={microSite.logo_url} alt="Logo" className="h-14" />
        <p className="text-lg font-bold text-gray-500">{microSite.partner_name}</p>
        <div className="flex items-center space-x-2">
          <CircleCheck className="size-8 text-blue-550" />
          <p className="text-2xl font-bold">
            {isRequestMode ? t("Ihre Anfrage wurde gesendet") : t("Sie sind eingeplant")}
          </p>
        </div>
        <p className="text-center text-gray-500">
          {isRequestMode
            ? t(
                "Ihre Terminanfrage wurde an die Apotheke gesendet. Sie erhalten eine E-Mail, sobald über die Anfrage entschieden wurde.",
              )
            : t("Eine Kalendereinladung wurde an ihre E-Mail-Adresse gesendet.")}
        </p>
        {type?.request_hint_text && isRequestMode ? (
          <div className="flex gap-2 rounded-md bg-blue-50 p-3 text-sm text-blue-900">
            <p>{type.request_hint_text}</p>
          </div>
        ) : null}
        <div className="flex flex-col space-y-4 rounded-lg border p-4">
          {infos.map((info) => (
            <div key={info.label} className="flex items-center space-x-2">
              {info.icon}
              <p>{info.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface Props {
  mode?: "booking" | "registration";
  partnerLanding?: PartnerLanding;
  appointmentType?: PartnerAppointmentType | null;
  medicalTestProfileGuid?: string;
  initialAppointmentTypes?: PartnerAppointmentType[];
}
